import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AvailabilitySidebar = ({ 
  onPresetApply, 
  availabilityStats, 
  upcomingImpact,
  onPatternSave,
  savedPatterns 
}) => {
  const [showPatternModal, setShowPatternModal] = useState(false);
  const [patternName, setPatternName] = useState('');

  const presets = [
    {
      id: 'weekdays',
      name: 'Nur Werktage',
      description: 'Mo-Fr, 9:00-17:00',
      icon: 'Calendar',
      pattern: 'weekdays_9_17'
    },
    {
      id: 'weekends',
      name: 'Nur Wochenenden',
      description: 'Sa-So, ganztägig',
      icon: 'Coffee',
      pattern: 'weekends_full'
    },
    {
      id: 'early_shift',
      name: 'Frühschicht',
      description: 'Mo-Fr, 6:00-14:00',
      icon: 'Sunrise',
      pattern: 'weekdays_6_14'
    },
    {
      id: 'late_shift',
      name: 'Spätschicht',
      description: 'Mo-Fr, 14:00-22:00',
      icon: 'Sunset',
      pattern: 'weekdays_14_22'
    },
    {
      id: 'night_shift',
      name: 'Nachtschicht',
      description: 'Mo-Fr, 22:00-6:00',
      icon: 'Moon',
      pattern: 'weekdays_22_6'
    },
    {
      id: 'flexible',
      name: 'Flexibel',
      description: 'Alle Tage, 8:00-20:00',
      icon: 'Clock',
      pattern: 'all_days_8_20'
    }
  ];

  const handlePresetClick = (preset) => {
    onPresetApply(preset?.pattern);
  };

  const handleSavePattern = () => {
    if (patternName?.trim()) {
      onPatternSave(patternName?.trim());
      setPatternName('');
      setShowPatternModal(false);
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getImpactIcon = (impact) => {
    switch (impact) {
      case 'high': return 'AlertTriangle';
      case 'medium': return 'AlertCircle';
      case 'low': return 'CheckCircle';
      default: return 'Info';
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Quick Presets */}
      <div className="bg-card rounded-lg border border-border shadow-card p-4">
        <h3 className="font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Zap" size={18} className="mr-2" />
          Schnellvorlagen
        </h3>
        <div className="space-y-2">
          {presets?.map(preset => (
            <Button
              key={preset?.id}
              variant="ghost"
              onClick={() => handlePresetClick(preset)}
              className="w-full justify-start p-3 h-auto"
            >
              <div className="flex items-start space-x-3">
                <Icon name={preset?.icon} size={16} className="mt-0.5 text-primary" />
                <div className="text-left">
                  <div className="font-medium text-sm">{preset?.name}</div>
                  <div className="text-xs text-muted-foreground">{preset?.description}</div>
                </div>
              </div>
            </Button>
          ))}
        </div>
      </div>
      {/* Availability Statistics */}
      <div className="bg-card rounded-lg border border-border shadow-card p-4">
        <h3 className="font-semibold text-foreground mb-4 flex items-center">
          <Icon name="BarChart3" size={18} className="mr-2" />
          Verfügbarkeitsstatistik
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Verfügbare Stunden</span>
            <span className="font-medium text-success">{availabilityStats?.availableHours}h</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Nicht verfügbar</span>
            <span className="font-medium text-error">{availabilityStats?.unavailableHours}h</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Bevorzugte Zeiten</span>
            <span className="font-medium text-primary">{availabilityStats?.preferredHours}h</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Bedingt verfügbar</span>
            <span className="font-medium text-warning">{availabilityStats?.conditionalHours}h</span>
          </div>
          <div className="pt-2 border-t border-border">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-foreground">Abdeckung</span>
              <span className="font-semibold text-foreground">
                {Math.round((availabilityStats?.availableHours / 168) * 100)}%
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Upcoming Schedule Impact */}
      <div className="bg-card rounded-lg border border-border shadow-card p-4">
        <h3 className="font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Calendar" size={18} className="mr-2" />
          Auswirkungen auf Termine
        </h3>
        <div className="space-y-3">
          {upcomingImpact?.length > 0 ? (
            upcomingImpact?.map((impact, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
                <Icon 
                  name={getImpactIcon(impact?.level)} 
                  size={16} 
                  className={`mt-0.5 ${getImpactColor(impact?.level)}`} 
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{impact?.date}</div>
                  <div className="text-xs text-muted-foreground mt-1">{impact?.description}</div>
                  {impact?.suggestion && (
                    <div className="text-xs text-primary mt-1">{impact?.suggestion}</div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4">
              <Icon name="CheckCircle" size={24} className="mx-auto text-success mb-2" />
              <p className="text-sm text-muted-foreground">Keine Konflikte erkannt</p>
            </div>
          )}
        </div>
      </div>
      {/* Saved Patterns */}
      <div className="bg-card rounded-lg border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-foreground flex items-center">
            <Icon name="Bookmark" size={18} className="mr-2" />
            Gespeicherte Muster
          </h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="Plus"
            iconSize={14}
            onClick={() => setShowPatternModal(true)}
          >
            Speichern
          </Button>
        </div>
        <div className="space-y-2">
          {savedPatterns?.length > 0 ? (
            savedPatterns?.map((pattern, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                <span className="text-sm text-foreground">{pattern?.name}</span>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Play"
                    iconSize={12}
                    onClick={() => onPresetApply(pattern?.data)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    iconSize={12}
                    className="text-error hover:text-error"
                  />
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">
              Keine gespeicherten Muster
            </p>
          )}
        </div>
      </div>
      {/* Bulk Operations */}
      <div className="bg-card rounded-lg border border-border shadow-card p-4">
        <h3 className="font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Settings" size={18} className="mr-2" />
          Massenoperationen
        </h3>
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Copy"
            iconPosition="left"
            iconSize={14}
            className="w-full justify-start"
          >
            Woche kopieren
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="RotateCcw"
            iconPosition="left"
            iconSize={14}
            className="w-full justify-start"
          >
            Zurücksetzen
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            iconSize={14}
            className="w-full justify-start"
          >
            Exportieren
          </Button>
        </div>
      </div>
      {/* Save Pattern Modal */}
      {showPatternModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-modal p-6 w-96">
            <h3 className="font-semibold text-foreground mb-4">Muster speichern</h3>
            <input
              type="text"
              value={patternName}
              onChange={(e) => setPatternName(e?.target?.value)}
              placeholder="Mustername eingeben"
              className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground mb-4"
            />
            <div className="flex space-x-2">
              <Button
                variant="default"
                onClick={handleSavePattern}
                disabled={!patternName?.trim()}
                className="flex-1"
              >
                Speichern
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPatternModal(false)}
                className="flex-1"
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilitySidebar;